import React, { Component } from 'react';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import makeQuery from 'yoda-core-components/lib/navigation/query/makeQuery';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import ArrowRight from 'yoda-core-components/lib/assets/svg/ArrowRight.svg';
import ArrowLeft from 'yoda-core-components/lib/assets/svg/ArrowLeft.svg';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import Button from 'yoda-core-components/lib/components/Button';
import * as styles from './TrendListZone.css';

const cx = classNames.bind(styles);

const add = (param1, param2) => param1 + param2;
const subtract = (param1, param2) => param1 - param2;

const calculateScrollPixel = ({
    productPanesDisplayed,
    totalProductPanes,
    scrollPixels,
    scrolledPanes,
    productPaneWidth,
    scrollToRight,
    remainingWrapperWidth,
    disableRightArrow,
}) => {
    const operator = scrollToRight ? subtract : add;

    // Calculate remaining(hidden panes) product panes in container to display
    const remainingProductsPanes = scrollToRight
        ? totalProductPanes - productPanesDisplayed - scrolledPanes
        : scrolledPanes;

    // Calculate product panes to show. It depends on the remaining panes and container size.
    /* istanbul ignore next */
    const numberOfPanesToMove =
        remainingProductsPanes >= productPanesDisplayed
            ? productPanesDisplayed
            : remainingProductsPanes;

    // Calculate next scroll position
    let scrollX = numberOfPanesToMove * productPaneWidth;

    // Calculate the total number of panes which are scrolled
    const productPanesScrolled = scrollToRight
        ? scrolledPanes + numberOfPanesToMove
        : scrolledPanes - numberOfPanesToMove;

    // Calculate the total panes which are displaced from its original position
    const migratedProductPanes = scrollToRight
        ? add(productPanesScrolled, productPanesDisplayed)
        : productPanesScrolled;

    if (totalProductPanes === migratedProductPanes || disableRightArrow) {
        scrollX -= remainingWrapperWidth;
    }
    const scrollContentBy = operator(scrollPixels, scrollX);
    return {
        scrollContentBy,
        migratedProductPanes,
        productPanesScrolled,
    };
};

class TrendListZone extends Component {
    static defaultProps = {
        listData: [],
    };

    static propTypes = {
        location: PropTypes.shape({
            pathname: PropTypes.string.isRequired,
            basename: PropTypes.string.isRequired,
            query: PropTypes.objectOf(PropTypes.object).isRequired,
            search: PropTypes.string.isRequired,
        }).isRequired,
        listData: PropTypes.arrayOf(PropTypes.object),
        loadTrendingItem: PropTypes.func.isRequired,
    };

    static getDerivedStateFromProps(props, state) {
        const totalCount = get(props, 'listData.length', 0);
        const isLoaded = state.totalCount === 0 && totalCount > 0;
        let { disableLeftArrow, disableRightArrow } = state;
        if (totalCount <= 3) {
            disableLeftArrow = true;
            disableRightArrow = true;
        } else if (isLoaded) {
            disableLeftArrow = true;
            disableRightArrow = false;
        }
        return {
            totalCount,
            isLoaded,
            disableLeftArrow,
            disableRightArrow,
        };
    }

    constructor() {
        super();
        this.state = {
            listStyle: {},
            disableLeftArrow: true,
            disableRightArrow: false,
            totalCount: 0,
        };
        this.scrollStatus = {
            scrollPixels: 0,
            scrolledPanes: 0,
        };
        this.rightScrollCartridge = this.scrollCartridge.bind(this, true);
        this.leftScrollCartridge = this.scrollCartridge.bind(this, false);
    }

    scrollCartridge = (scrollToRight) => {
        const { totalCount } = this.state;
        const pane = this.productPane;
        /* below value should modified based on
           "product-card-block -> ul -> li ->  margin: 0 14px 0 0" css margin value
        */
        const extraMargin = 14;
        const wrapperWidth = this.wrapper.getBoundingClientRect().width;
        const productPaneWidth = pane.getBoundingClientRect().width + extraMargin;
        const { scrollPixels, scrolledPanes } = this.scrollStatus;

        // Calculate number of products displayed in the container
        const totalProductPanes = totalCount;
        const result = wrapperWidth / productPaneWidth;
        const productPanesDisplayed = Math.floor(result);
        const remainingWrapperWidth =
            (result - productPanesDisplayed).toFixed(2) * productPaneWidth;

        const mountedCartrdigeInfo = {
            productPanesDisplayed,
            totalProductPanes,
            scrollPixels,
            scrolledPanes,
            productPaneWidth,
            scrollToRight,
            remainingWrapperWidth,
            disableRightArrow: this.state.disableRightArrow,
        };

        const {
            scrollContentBy,
            productPanesScrolled,
            migratedProductPanes,
        } = calculateScrollPixel(mountedCartrdigeInfo);

        this.scrollStatus.scrollPixels = scrollContentBy;
        this.scrollStatus.scrolledPanes = productPanesScrolled;
        this.setState({
            disableLeftArrow: migratedProductPanes === 0,
            disableRightArrow: migratedProductPanes === totalProductPanes,
            listStyle: {
                transform: `translate(${scrollContentBy}px)`,
            },
        });
    };

    loadItem = (item) => {
        const { id, accountId, shareToken: token } = item;
        this.props.loadTrendingItem(id, token, accountId);
    };

    /* istanbul ignore next */
    trendingListRenderer = (data, currentId) => {
        const imageUrl = get(data, 'items[0].imageUrl', '');
        const sameId = currentId === data.id;
        let clickFn = {};
        if (!sameId) {
            clickFn = {
                onClick: (e) => {
                    e.preventDefault();
                    this.loadItem(data);
                },
            };
        }
        return (
            /* eslint-disable react/jsx-props-no-spreading */
            <li
                className={`${cx({ active: sameId })} ${dt([
                    'inline-block',
                    'list-none',
                    'w-[262px]',
                    'my-0',
                    'mr-4',
                    'ml-0',
                    'cursor-pointer',
                    'transition-all',
                    'duration-200',
                    'ease-in-out',
                ])}`}
                {...clickFn}
            >
                <span
                    ref={(productPane) => {
                        this.productPane = productPane;
                    }}
                >
                    <img
                        src={imageUrl}
                        alt={data.name}
                        className={dt([' w-[262px]', 'min-h-[262px]'])}
                    />
                    <div
                        className={`${cx('text', 'two')} ${dt([
                            'text-black',
                            'block',
                            'relative',
                            'bg-white',
                            'w-[246px]',
                            'text-center',
                            '-mt-4',
                            'mr-0',
                            'mb-0',
                            '!mx-2',
                            'transition-all',
                            'duration-200',
                            'ease-in-out',
                            'after:content-[""]',
                            'after:h-1.5',
                            'after:w-[246px]',
                            'after:block',
                            'after:bg-gray-70',
                            'after:transition-all',
                            'after:duration-200',
                            'after:ease-in-out',
                        ])}`}
                    >
                        <p className={dt(['m-0', 'pb-2', 'p-3', 'text-normal'])}>{data.name}</p>
                    </div>
                    <div
                        className={`${cx('triangle', 'two')} ${dt([
                            'w-0',
                            'h-0',
                            '!border-l-[10px]',
                            '!border-solid',
                            '!border-l-transparent',
                            'border-r-[10px]',
                            '!border-r-transparent',
                            '!border-t-[10px]',
                            '!border-t-transparent',
                            'my-0',
                            'mx-auto',
                            'transition-all',
                            'duration-200',
                            'ease-in-out',
                            'hover:!border-t-[14px]',
                            'hover:!border-solid',
                            'hover:!border-b-black',
                            'hover:my-0',
                            'hover:mx-auto',
                            'hover:!border-r-transparent',
                        ])}`}
                    />
                </span>
            </li>
        );
    };

    /* istanbul ignore next */
    render() {
        const { listData, location } = this.props;
        const search = get(location, 'search', '');
        const query = makeQuery(search);
        const currentId = get(query, 'id', '');
        const { listStyle, disableLeftArrow, disableRightArrow } = this.state;
        const mobLeftFade = (
            <div className={dt(['w-[108px]', 'h-[264px]', 'absolute', 'left-0'])} />
        );
        const mobRightFade = (
            <div className={dt(['w-[108px]', 'h-[264px]', 'absolute', 'right-0'])} />
        );
        const leftArrowClass = cx({ iconClass: true, disabledArrow: disableLeftArrow });
        const rightArrowClass = cx({ iconClass: true, disabledArrow: disableRightArrow });

        return (
            <div
                className={`${cx('product-card-wrapper-recommendations')} ${dt([
                    'flex',
                    'flex-wrap',
                    'sm:!transform-none',
                    'inline',
                    'overflow-auto',
                    'whitespace-nowrap',
                ])}`}
                ref={(wrapper) => {
                    this.wrapper = wrapper;
                }}
                style={listStyle}
            >
                <div className={dt(['relative', 'w-full', 'sm:hidden', 'lg:block'])}>
                    <span style={{ visibility: disableLeftArrow ? 'hidden' : 'visible' }}>
                        <Button
                            buttonType="Link"
                            className={dt(['absolute', 'top-[128px]', 'left-[22px]', 'z-[1]'])}
                            onClick={this.leftScrollCartridge}
                        >
                            <Icon
                                iconType="svg"
                                className={dt([
                                    '!bg-white',
                                    'arrow-icon',
                                    'rounded-half',
                                    'w-10',
                                    'h-10',
                                    'p-2',
                                    'shadow-elevation1',
                                ])}
                                width="24px"
                                height="24"
                                viewBox="0 0 20 20"
                                pathClassName={leftArrowClass}
                            >
                                <ArrowLeft />
                            </Icon>
                        </Button>
                    </span>
                    <span style={{ visibility: disableRightArrow ? 'hidden' : 'visible' }}>
                        <Button
                            buttonType="Link"
                            className={dt(['absolute', 'top-[128px]', 'right-[22px]', 'z-[1]'])}
                            onClick={this.rightScrollCartridge}
                        >
                            <Icon
                                iconType="svg"
                                className={dt([
                                    '!bg-white',
                                    'arrow-icon',
                                    'rounded-half',
                                    'w-10',
                                    'h-10',
                                    'p-2',
                                    'shadow-elevation1',
                                ])}
                                width="24px"
                                height="24px"
                                viewBox="0 0 20 20"
                                pathClassName={rightArrowClass}
                            >
                                <ArrowRight />
                            </Icon>
                        </Button>
                    </span>
                    {!disableRightArrow && mobRightFade}
                    {!disableLeftArrow && mobLeftFade}
                </div>
                <div
                    className={`${cx('product-card-block')} ${dt([
                        'sm:overflow-auto',
                        'lg:overflow-hidden',
                        'mt-4',
                        'relative',
                    ])}`}
                    id="product-card-section"
                >
                    <ul
                        style={listStyle}
                        className={dt([
                            'transition-transform',
                            'duration-[400]',
                            'ease-in-out',
                            'w-full',
                            'max-w-[100vw]',
                        ])}
                    >
                        {listData &&
                            listData.map((data) => this.trendingListRenderer(data, currentId))}
                    </ul>
                </div>
            </div>
        );
    }
}

export default TrendListZone;
